import { ToastOptions } from "react-toastify";
import PartSiteSourceSlicersModel from "../models/PlanningParameters/PartSiteSourceSlicers.model";
import { ReplPlanSelectedSlicerModel } from "../models/ReplenishmentPlan/ReplPlanSlicer.model";
import SourceMasterSlicersModel from "../models/MasterTables/SourceMasterSlicers";
import {
  ANNUAL_SUMMARY_TAB_ID,
  FORECAST_REVIEW_DEFAULT_SKU_BRANDS,
  FORECAST_REVIEW_DEFAULT_SKU_SUB_BRANDS,
  FORECAST_REVIEW_DEFAULT_SKU_VOLUME_SIZES,
  FORECAST_REVIEW_DEFAULT_YEAR,
  FORECAST_REVIEW_TAB_ID,
  LSO_DEFAULT_GROUP_POLICY,
  LSO_DEFAULT_NUM_OF_WEEKS,
  LSO_DEFAULT_PART_CODE,
  LSO_DEFAULT_SITE_CODES,
  PROMO_MASTER_TAB_ID,
  PROMOTION_PLANNING_TAB_ID,
  REPL_PLAN_DEFAULT_NUM_OF_WEEKS,
  REPL_PLAN_DEFAULT_PART_CODE,
  REPL_PLAN_DEFAULT_SITE_CODE,
  TPP_DEFAULT_SKU,
  TPP_DEFAULT_SKU_BRAND,
  TPP_DEFAULT_SKU_BRAND_SUBBRAND,
  TPP_DEFAULT_SKU_ID,
  TPP_DEFAULT_YEAR,
  TRADE_SPEND_REPORT_TAB_ID,
  VOLUME_FORECAST_TAB_ID,
} from "./constants";
import { LSOSelectedSlicerModel } from "../models/LotSizeOptimization/LSOSlicer.model";
import { VolumeForecastSelectedSlicerModel } from "../models/TradeAndPromoPlanning/VolumeForecastSlicer.model";
import { PromotionPlanningSelectedSlicerModel } from "../models/TradeAndPromoPlanning/PromotionPlanningSlicer.model";
import { PromoMasterSelectedSlicerModel } from "../models/TradeAndPromoPlanning/PromoMasterSlicer.model";
import { ForecastReviewSelectedSlicerModel } from "../models/TradeAndPromoPlanning/ForecastReviewSlicer.model";
import { TradeSpendReportSelectedSlicerModel } from "../models/TradeAndPromoPlanning/TradeSpendReportSlicer.model";
import { AnnualSummarySelectedSlicerModel } from "../models/TradeAndPromoPlanning/AnnualSummarySlicer.model";

/**
 * Stores / returns slicers for Planning Parameters tab in browser's local storage
 * @returns {PartSiteSourceSlicersModel} New / updated Part Site Source slicers
 */
export const managePlanningParametersSlicersInStorage =
  (): PartSiteSourceSlicersModel => {
    const defaultSlicers: PartSiteSourceSlicersModel = {
      part_name: ["All"],
      source_value: ["All"],
      location_name: ["All"],
    };

    try {
      const storageSelectedSlicers = sessionStorage.getItem("selectedSlicers");

      if (storageSelectedSlicers) {
        return JSON.parse(storageSelectedSlicers);
      } else {
        sessionStorage.setItem(
          "selectedSlicers",
          JSON.stringify(defaultSlicers)
        );
        return defaultSlicers;
      }
    } catch (error: any) {
      console.error(error);
      sessionStorage.setItem("selectedSlicers", JSON.stringify(defaultSlicers));
      return defaultSlicers;
    }
  };

/**
 * Stores / returns slicers for Source Master grid in browser's local storage
 * @returns {SourceMasterSlicersModel} New / updated Source Master grid slicers
 */
export const manageSourceMasterSlicersInStorage =
  (): SourceMasterSlicersModel => {
    const defaultSlicers: SourceMasterSlicersModel = {
      site_code: ["All"],
      supplier_code: ["All"],
    };

    try {
      const storageSelectedSlicers = sessionStorage.getItem(
        "selectedSourceMasterSlicers"
      );

      if (storageSelectedSlicers) {
        return JSON.parse(storageSelectedSlicers);
      } else {
        sessionStorage.setItem(
          "selectedSourceMasterSlicers",
          JSON.stringify(defaultSlicers)
        );
        return defaultSlicers;
      }
    } catch (error: any) {
      console.error(error);
      sessionStorage.setItem(
        "selectedSourceMasterSlicers",
        JSON.stringify(defaultSlicers)
      );
      return defaultSlicers;
    }
  };

/**
 * Returns default slicers for Replenishment Plan tab
 * @returns {ReplPlanSelectedSlicerModel} Replenishment Plan tab slicers
 */
export const getDefaultReplPlanSlicers = (): ReplPlanSelectedSlicerModel => {
  const defaultReplPlanSlicers: ReplPlanSelectedSlicerModel = {
    site_code: REPL_PLAN_DEFAULT_SITE_CODE,
    part_code: REPL_PLAN_DEFAULT_PART_CODE,
    number_of_weeks: REPL_PLAN_DEFAULT_NUM_OF_WEEKS,
  };

  return defaultReplPlanSlicers;
};

// Returns default slicers for Control Tower tab
export const getDefaultControlTowerSlicersInStorage = () => {
  const defaultSlicers = {
    part_names: ["All"],
    location_names: ["All"],
    number_of_weeks: 25,
  };

  return defaultSlicers;
};

/**
 * Returns default slicers for Lot Size Optimization tab
 * @returns {LSOSelectedSlicerModel} Lot Size Optimization tab slicers
 */
export const getDefaultLSOSlicers = (): LSOSelectedSlicerModel => {
  const defaultLSOSlicers: LSOSelectedSlicerModel = {
    site_codes: LSO_DEFAULT_SITE_CODES,
    part_code: LSO_DEFAULT_PART_CODE,
    group_policy_name: LSO_DEFAULT_GROUP_POLICY,
    number_of_weeks: LSO_DEFAULT_NUM_OF_WEEKS,
  };

  return defaultLSOSlicers;
};

/**
 * Returns default slicers for tabs of Forecast Entry App
 * @param {string} tabId Tab ID
 * @returns {any} Default slicers for tab ID passed as an argument.
 */
export const getDefaultSlicersForForecastEntryApp = (tabId: string): any => {
  switch (tabId) {
    case VOLUME_FORECAST_TAB_ID: {
      const defaultSlicers: VolumeForecastSelectedSlicerModel = {
        sku: TPP_DEFAULT_SKU,
        brand: TPP_DEFAULT_SKU_BRAND,
        brandSubBrand: TPP_DEFAULT_SKU_BRAND_SUBBRAND,
        year: TPP_DEFAULT_YEAR,
      };

      return defaultSlicers;
    }

    case PROMOTION_PLANNING_TAB_ID: {
      const defaultSlicers: PromotionPlanningSelectedSlicerModel = {
        sku_ids: ["All"],
        year: TPP_DEFAULT_YEAR,
      };

      return defaultSlicers;
    }

    case PROMO_MASTER_TAB_ID: {
      const defaultSlicers: PromoMasterSelectedSlicerModel = {
        skus: [TPP_DEFAULT_SKU_ID],
      };

      return defaultSlicers;
    }

    case FORECAST_REVIEW_TAB_ID: {
      const defaultSlicers: ForecastReviewSelectedSlicerModel = {
        sku_brands: FORECAST_REVIEW_DEFAULT_SKU_BRANDS,
        sku_sub_brands: FORECAST_REVIEW_DEFAULT_SKU_SUB_BRANDS,
        sku_volume_sizes: FORECAST_REVIEW_DEFAULT_SKU_VOLUME_SIZES,
        year: FORECAST_REVIEW_DEFAULT_YEAR,
      };

      return defaultSlicers;
    }

    case ANNUAL_SUMMARY_TAB_ID: {
      const defaultSlicers: AnnualSummarySelectedSlicerModel = {
        brand: ["All"],
        subBrand: ["All"],
        volumeSize: ["All"],
      };

      return defaultSlicers;
    }

    case TRADE_SPEND_REPORT_TAB_ID: {
      const defaultSlicers: TradeSpendReportSelectedSlicerModel = {
        skus: ["All"],
        brands: ["All"],
        promotions: ["All"],
        promotionType: "All"
      };

      return defaultSlicers;
    }

    default:
      return {};
  }
};

/**
 * Returns number in USA format (e.g. 1,000,000)
 * @param {number} value Input number
 * @param {number} precision Numbers after decimal
 * @returns {string} Formatted number string
 */
export const usaNumberFormatter = (
  value: any,
  precision: number = 2
): string | null => {
  if (value === null || value === undefined || typeof value !== "number") {
    return null;
  }

  const roundedNumStr: string = value
    ? value.toFixed(precision)
    : (0).toFixed(precision);
  const usaFormattedNum: string = roundedNumStr
    .replaceAll(/\B(?=(\d{3})+(?!\d))/g, ",")
    .replaceAll(/\.00/g, "");
  return usaFormattedNum;
};

/**
 * Returns react-toastify toast options as per toast type
 * @param {"success" | "error" | "warning" | "info" | "loading"} type Toast type
 * @returns {ToastOptions<any>} Toast Options
 */
export const getToastOptions = (
  type: "success" | "error" | "warning" | "info" | "loading"
): ToastOptions<any> => {
  let toastConfig: ToastOptions = {
    autoClose: 2000,
    closeButton: true,
  };

  switch (type) {
    case "success":
      toastConfig.className =
        "segoe-font text-dark bg-success-subtle border border-success";
      break;

    case "error":
      toastConfig.className =
        "segoe-font text-dark bg-danger-subtle border border-danger";
      break;

    case "warning":
      toastConfig.className =
        "segoe-font bg-warning-subtle border border-warning";
      toastConfig.style = {
        color: "#00833E",
      };
      break;

    case "info":
    case "loading":
      toastConfig.autoClose = type === "loading" ? undefined : 2000;
      toastConfig.closeButton = !(type === "loading");
      toastConfig.className =
        "segoe-font custom-bg-subtle border border-primary";
      toastConfig.style = {
        color: "#00833E",
      };
      break;

    default:
      break;
  }

  return toastConfig;
};
/**
 * Remove newly inserted rows by their ID
 * @param {any} insertedRows Newly inserted rows
 * @param {any[]} selectedRowIds IDs of selected rows
 * @returns {any} Updated row data
 */
export const removeInsertedRowsById = (
  insertedRows: any,
  selectedRowIds: any[]
): any => {
  const insertedRowIds: any[] = selectedRowIds.filter((rowId) =>
    rowId.toString().startsWith("new_")
  );
  const newInsertedRows: any = {};

  for (let id in insertedRows) {
    if (insertedRowIds.indexOf(id) === -1) {
      newInsertedRows[id] = insertedRows[id];
    }
  }

  return newInsertedRows;
};

/**
 * Returns row style for newly added rows in AG Grid
 * @param {any} params Row Style event params
 * @param {string} idFieldName Row ID field name
 * @returns CSS-styles in JSON format
 */
export const newGridRowStyleHandler = (
  params: any,
  idFieldName: string
): any => {
  if (params.data[idFieldName]?.toString().startsWith("new_")) {
    return {
      backgroundColor: "#D9FFD9",
    };
  }
};

/**
 * Returns API data according to the required AG Grid row order.
 * @param {{ [key: string]: { label: string; rowIndex: string; } }} rowLabelsAndIndexes Object with labels and indexes of row.
 * @param {any[]} apiData Fetched data from Repl. Plan Summary / Promotion Planning pivoted API
 * @returns {any[]} Re-ordered API data array
 */
export const getReOrderedAPIData = (
  rowLabelsAndIndexes: {
    [key: string]: { label: string; rowIndex: number };
  },
  apiData: any[]
): any[] => {
  let reOrderedAPIData: any[] = structuredClone(apiData);

  apiData.forEach((rowData) => {
    const rowIndex: number = rowLabelsAndIndexes[rowData.variable].rowIndex;
    rowData['rowIndex'] = rowIndex;
  });
  reOrderedAPIData = apiData.sort((a, b)=>Number(a.rowIndex)-Number(b.rowIndex));
  return reOrderedAPIData;
};

/**
 * Returns sorted USA date string list.
 * @param {string[]} usaDateStrList USA date string list.
 * @returns {string[]} Sorted USA date string list.
 */
export const getSortedUSADateStrList = (usaDateStrList: string[]): string[] => {
  const sortedUSADateStrList: string[] = usaDateStrList.sort(
    (dateStr1, dateStr2) => {
      const splitDate1 = dateStr1.split("/");
      const splitDate2 = dateStr2.split("/");

      const isoDateStr1 = `${splitDate1[2]}${splitDate1[0]}${splitDate1[1]}`;
      const isoDateStr2 = `${splitDate2[2]}${splitDate2[0]}${splitDate2[1]}`;

      return isoDateStr1.localeCompare(isoDateStr2);
    }
  );

  return sortedUSADateStrList;
};

export const getCurrentDateTime = () =>{
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // Months are 0-based
  const day = now.getDate();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const seconds = now.getSeconds();

  const formatted = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

  return formatted;
}

export const createDescription = (tabName: string, status: string, changesDict?: any, gridData?: any) => {
    const updatedChanges: any = [];
    
    if (tabName === "volumeForecast"){
        Object.keys(changesDict).forEach((c1: any) => {
            Object.keys(changesDict[c1]).forEach((c2) => {
                const row = gridData.find((row: any) => row.variable === c1);
                if (row && row[c2]!==changesDict[c1][c2]) {
                    const entry = `${c1}: ([${c2}] => old value: ${row[c2]} , new value: ${changesDict[c1][c2]})`;
                    updatedChanges.push(entry);
                }
            });
        });
        return updatedChanges;
    }else if(tabName === "promotionPlanning"){
      
      if(status === "update"){
        Object.keys(changesDict).forEach((c1:any)=>{
            Object.keys(changesDict[c1]).forEach((c2:any) => {
                  const rowObj:any = Object.values(gridData).filter((row: any) => row.promotion_calendar_id === parseInt(c1));

                if (rowObj.length>0) {
                  if (['Week Start Date', 'Week End Date'].includes(c2)){
                    let maps = c2 === "Week Start Date" ? "week_start_date"
                     : c2 === "Week End Date" ? "week_end_date" : c2;

                    const entry = `Promotion  ID-${rowObj[0].promotion_id}: ([${c2}] => old value: ${rowObj[0][maps]} , new value: ${changesDict[c1][c2]})`;
                    updatedChanges.push(entry);
                  }
                }else{
                    const entry = `new_row: ([${c2}] => ${changesDict[c1][c2]})`;
                    updatedChanges.push(entry);
                }
            });
        });
      }else if(status === "delete"){
        changesDict.map((row:any)=>{
          updatedChanges.push(`Deleted the Promotion Id: ${row.promotion_id} for the week start date: ${row.week_start_date} and week end date: ${row.week_end_date} `)
        })
      }
        return updatedChanges;
    }else if(tabName === "longTermPromotion"){
      if (status === "update"){
        Object.keys(changesDict).forEach((c1:any)=>{
            Object.keys(changesDict[c1]).forEach((c2:any) => {
                const rowObj:any = Object.values(gridData).filter((row: any) => row.id === parseInt(c1));
                if (rowObj.length>0 && rowObj[0][c2]!==changesDict[c1][c2]) {
                    const entry = `EveryDay ID-${c1}: ([${c2}] => old value: ${rowObj[0][c2]} , new value: ${changesDict[c1][c2]})`;
                    updatedChanges.push(entry);
                }else{
                    const entry = `new_row: ([${c2}] : ${changesDict[c1][c2]})`;
                    updatedChanges.push(entry);
                }
            });
        });
      }else if(status === "delete"){

        changesDict.map((row:any)=>{
          updatedChanges.push(`Deleted the Everyday Id: ${row.id} with promotion name: ${row["Promotion Name"]} `)
        })
      }
        return updatedChanges;
    }else if(tabName === "shortTermPromotion"){
        if (status === "update"){
          Object.keys(changesDict).forEach((c1:any)=>{
            Object.keys(changesDict[c1]).forEach((c2:any) => {
                const rowObj:any = Object.values(gridData).filter((row: any) => row.id === parseInt(c1));
                if (rowObj.length>0 && rowObj[0][c2]!==changesDict[c1][c2]) {
                    const entry = `Promotion ID-${c1}: ([${c2}] => old value: ${rowObj[0][c2]} , new value: ${changesDict[c1][c2]})`;
                    updatedChanges.push(entry);
                }else{
                    const entry = `new_row: ([${c2}] : ${changesDict[c1][c2]})`;
                    updatedChanges.push(entry);
                }
            });
        });
        }else if(status === "delete"){
          changesDict.map((row:any)=>{
          updatedChanges.push(`Deleted the Promotion Id: ${row.id} with promotion name: ${row["Promotion Name"]} `)
        })
        }
        return updatedChanges;
    }
    
};